import React, {useEffect, useLayoutEffect, useRef, useState, useCallback, useMemo} from "react";
import {apiV2} from "@/api/axiosConfig";
import {FilterQueryParams} from "@/components/paginated-selector/filters/types";
import Supercluster from "supercluster";
import {IncidentFeedFilter} from "@/components/paginated-selector/filters/IncidentFeedFilter";
import {IncidentTypeTagFilter} from "@/components/paginated-selector/filters/IncidentTypeTagFilter";
import {CountryLocationFilter} from "@/components/paginated-selector/filters/CountryLocationFilter";
import {SeverityFilter} from "@/components/paginated-selector/filters/SeverityFilter";
import {TimeRangeFilter} from "@/components/paginated-selector/filters/TimeRangeFilter";
import {OptionsFilter} from "@/components/paginated-selector/filters/OptionsFilter";
import {useSearchParams} from "react-router-dom";
import {useDebounce} from "use-debounce";
import {IncidentDetailsModal} from "@/components/modals/IncidentDetailsModal";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import {IncidentSeverityDisplay} from "@/components/incident-severity/IncidentSeverityDisplay";
import KeyIcon from "@mui/icons-material/Key";
import ModalV2 from "@/components/portal/ModalV2";
import {WebSocketManager, ConnectionState, MessageHandlers} from "@/utils/WebSocketManager";
import {usePlatformAPI} from "@/api/PlatformProvider";

type Incident = {
    id: string;
    categoryName: string;
    typeName: string;
    severity: string;
    displaySeverity?: string;
    summary: string;
    description: string;
    countryISO: string;
    town?: string;
    feedID: string;
    tags: string[];
    startedAt: string;
    endedAt?: string;
    reportedAt?: string;
    updatedAt?: string;
    ongoing: boolean;
    timezone?: string;
    position: {
        type: string;
        coordinates: [number, number]; // [lng, lat]
    };
    priority?: number;
};

type ClusterProperties = {
    cluster: boolean;
    cluster_id?: number;
    point_count?: number;
    incidents?: Incident[];
    avgSeverity?: number;
};

type IncidentFeature = GeoJSON.Feature<GeoJSON.Point, { incident: Incident }>;
type ClusterFeature = Supercluster.ClusterFeature<ClusterProperties>;
type PointFeature = Supercluster.PointFeature<ClusterProperties>;


const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAP_API_KEY || "";

// Toggle staggered pulse timing - set to false for synchronized pulses
const STAGGERED_PULSES = true;

// Border configuration - helps separate overlapping points
const BORDER_WIDTH = 0.05; // Thickness of border (0.0 to 0.1 recommended)
const BORDER_LIGHTNESS = 0.5; // Mix severity color with white (0.0 = original color, 1.0 = white)

const getSeverityColor = (severity: string): [number, number, number] => {
    switch (severity?.toLowerCase()) {
        case "extreme":
            return [147, 51, 234];
        case "major":
            return [220, 38, 38];
        case "moderate":
            return [234, 88, 12];
        case "limited":
            return [216, 183, 0];
        case "insignificant":
            return [22, 163, 74];
        default:
            return [107, 114, 128];
    }
};

const getSeverityValue = (severity: string): number => {
    switch (severity?.toLowerCase()) {
        case "extreme":
            return 6;
        case "major":
            return 5;
        case "moderate":
            return 4;
        case "limited":
            return 3;
        case "insignificant":
            return 2;
        default:
            return 1; // unknown
    }
};

const getIncidentPriority = (incident: Incident): number => {
    if (incident.displaySeverity) return getSeverityValue(incident.displaySeverity);
    if (incident.priority != null) return incident.priority;
    for (const tag of (incident.tags || [])) {
        switch (tag.toLowerCase()) {
            case "high_severity":   return 6;
            case "medium_severity": return 4;
            case "low_severity":    return 2;
            case "unknown_severity": return 1;
        }
    }
    if (incident.severity) return getSeverityValue(incident.severity);
    return 1;
};

// Maps priority (from getIncidentPriority) to a render color.
// Scale: 6=extreme/purple, 5=major/red, 4=moderate/orange,
//        3=limited/yellow, 2=insignificant/green, 1=unknown/grey
const getColorFromPriority = (value: number): [number, number, number] => {
    if (value >= 6) return [147, 51, 234];  // extreme / high_severity
    if (value >= 5) return [220, 38, 38];   // major
    if (value >= 4) return [234, 88, 12];   // moderate / medium_severity
    if (value >= 3) return [216, 183, 0];   // limited
    if (value >= 2) return [22, 163, 74];   // insignificant / low_severity
    return [107, 114, 128];                 // unknown
};

const getIncidentColor = (incident: Incident): [number, number, number] => {
    return getColorFromPriority(getIncidentPriority(incident));
};

export function IncidentMapPage() {
    const platformClient = usePlatformAPI();
    const [searchParams, setSearchParams] = useSearchParams();
    const [incidents, setIncidents] = useState<Incident[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [googleLoaded, setGoogleLoaded] = useState(false);
    const [popupVisible, setPopupVisible] = useState(false);
    const [popupPosition, setPopupPosition] = useState({x: 0, y: 0});
    const [popupIncidents, setPopupIncidents] = useState<Incident[]>([]);
    const [popupPage, setPopupPage] = useState(1);
    const [popupTotalCount, setPopupTotalCount] = useState(0);
    const popupAllIDsRef = useRef<string[]>([]);
    const modalFromPopupRef = useRef(false);
    const popupClampedRef = useRef(false);
    const POPUP_PAGE_SIZE = 5;
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({x: 0, y: 0});
    const [tooltipCount, setTooltipCount] = useState(0);
    const [tooltipIncidents, setTooltipIncidents] = useState<Incident[]>([]);
    const [showRiskRatingModal, setShowRiskRatingModal] = useState(false);
    const [feedCount, setFeedCount] = useState<number | null>(null);
    const hasAutoSelectedFeedRef = useRef(false);
    const [mapType, setMapType] = useState<string>(() => {
        // Load from localStorage or default to roadmap
        try {
            return localStorage.getItem("incidentMapType") || "roadmap";
        } catch {
            return "roadmap";
        }
    });
    const [rightClickPosition, setRightClickPosition] = useState<{lat: number; lng: number; x: number; y: number} | null>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const coordinatePopupRef = useRef<HTMLDivElement>(null);

    // WebSocket connection state
    const [wsConnectionState, setWsConnectionState] = useState<ConnectionState>(ConnectionState.DISCONNECTED);
    const [storeLoading, setStoreLoading] = useState(false);
    const wsManagerRef = useRef<WebSocketManager | null>(null);
    const pendingIncidentDetailsRef = useRef<{incidentIDs: string[], callback: (incidents: any[]) => void} | null>(null);

    // Fetch feed count and auto-select if only one feed exists
    useEffect(() => {
        if (hasAutoSelectedFeedRef.current) return;

        const fetchFeedCount = async () => {
            try {
                const response = await apiV2.get("incident-feeds", {
                    params: { page: 1, limit: 2 } // Only need to know if there's more than 1
                });
                const total = response.data.total || 0;
                setFeedCount(total);

                // If there's only one feed, auto-select it
                if (total === 1) {
                    const feeds = response.data.items || [];
                    if (feeds.length > 0) {
                        const feed = feeds[0];
                        setFilterParams(prev => {
                            // Only auto-select if no feed is currently selected
                            if (!prev.feedIDs || (Array.isArray(prev.feedIDs) && prev.feedIDs.length === 0)) {
                                hasAutoSelectedFeedRef.current = true;
                                return {
                                    ...prev,
                                    feedIDs: [feed.id],
                                    feedNames: [feed.name],
                                    feedType: feed.type
                                };
                            }
                            return prev;
                        });
                    }
                }
            } catch (error) {
                console.error("Failed to fetch feed count:", error);
                setFeedCount(1); // Default to showing filter on error
            }
        };

        fetchFeedCount();
    }, []); // Only run once on mount

    // Suppress Google Maps internal errors
    useEffect(() => {
        const handleError = (event: ErrorEvent) => {
            // Suppress Google Maps internal errors that happen during initialization
            if (event.message?.includes("can't access property") &&
                event.filename?.includes("maps.googleapis.com")) {
                event.preventDefault();
                return true;
            }
        };

        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            // Suppress Google Maps promise rejections
            if (event.reason?.message?.includes("maps.googleapis.com") ||
                event.reason?.stack?.includes("maps.googleapis.com")) {
                event.preventDefault();
            }
        };

        window.addEventListener("error", handleError);
        window.addEventListener("unhandledrejection", handleUnhandledRejection);
        return () => {
            window.removeEventListener("error", handleError);
            window.removeEventListener("unhandledrejection", handleUnhandledRejection);
        };
    }, []);

    const mapDivRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<google.maps.Map | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const glRef = useRef<WebGLRenderingContext | null>(null);
    const programRef = useRef<WebGLProgram | null>(null);
    const animationFrameRef = useRef<number>();
    const isDraggingRef = useRef(false);
    const overlayRef = useRef<google.maps.OverlayView | null>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const hoveredIncidentsRef = useRef<Set<string>>(new Set());
    const hoveredPointIdsRef = useRef<Set<string>>(new Set()); // Store all hovered point/cluster IDs
    // Pixel-space cache of rendered points — updated by draw(), used by hit-testing
    const renderedPointsRef = useRef<Array<{x: number; y: number; id: string; isCluster: boolean; incidents: Incident[]}>>([]);
    // Pending WS incident updates — flushed in a batch
    const pendingIncidentUpdatesRef = useRef<{created: Incident[]; updated: Incident[]}>({created: [], updated: []});
    const incidentFlushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Clamp popup to viewport after it renders
    useLayoutEffect(() => {
        if (!popupVisible || !popupRef.current) return;
        if (popupClampedRef.current) return;
        popupClampedRef.current = true;
        const el = popupRef.current;
        const margin = 16;
        const w = 320; // matches w-80
        const h = el.scrollHeight;
        const viewportWidth = document.documentElement.clientWidth;
        let {x, y} = popupPosition;
        if (x + w + margin > viewportWidth) x = viewportWidth - w - margin;
        if (y + h + margin > window.innerHeight) y = window.innerHeight - h - margin;
        if (x < margin) x = margin;
        if (y < margin) y = margin;
        if (x !== popupPosition.x || y !== popupPosition.y) {
            setPopupPosition({x, y});
        }
    }, [popupVisible, popupIncidents, popupPosition]);

    // Filters
    const getInitialFilters = useCallback(() => {
        // Start with default filters
        const defaultFilters = {timeRange: "1w"};

        // First try to get from URL params (for sharing/bookmarking)
        const urlFilters = searchParams.get("filters");
        if (urlFilters) {
            try {
                return JSON.parse(urlFilters);
            } catch {
                // Fall through to localStorage for options only
            }
        }

        // Load options and feed selection from localStorage
        try {
            const storedOptions = localStorage.getItem("incidentMapOptions");
            const storedFeed = localStorage.getItem("incidentFeedSelection");
            const options = storedOptions ? JSON.parse(storedOptions) : {};
            const feed = storedFeed ? JSON.parse(storedFeed) : {};
            if (storedOptions || storedFeed) {
                return {
                    ...defaultFilters,
                    ...(options.disableAnimations !== undefined && {disableAnimations: options.disableAnimations}),
                    ...(options.enableClustering !== undefined && {enableClustering: options.enableClustering}),
                    ...(feed.feedIDs?.length && {feedIDs: feed.feedIDs, feedNames: feed.feedNames, feedType: feed.feedType}),
                };
            }
        } catch {
            // Fall through to default
        }

        return defaultFilters;
    }, [searchParams]);

    const [filterParams, setFilterParams] = useState<FilterQueryParams>(getInitialFilters);
    const [debouncedFilterParams] = useDebounce(filterParams, 300);

    // Keep latest filters in ref for WebSocket callbacks
    const latestFiltersRef = useRef(debouncedFilterParams);
    useEffect(() => {
        latestFiltersRef.current = debouncedFilterParams;
    }, [debouncedFilterParams]);

    // Derive pulsesEnabled from filterParams (inverted - disableAnimations = true means pulsesEnabled = false)
    const pulsesEnabled = !filterParams.disableAnimations;
    const clusteringEnabled = filterParams.enableClustering === true || incidents.length >= 5000;

    // Initialize Supercluster with incidents data
    const supercluster = useMemo(() => {
        if (!clusteringEnabled || incidents.length === 0) return null;

        const index = new Supercluster<ClusterProperties>({
            radius: 50,
            maxZoom: 14,
            minZoom: 4,
            map: (props: any) => ({
                maxSeverity: getIncidentPriority(props.incident),
                count: 1,
                incident: props.incident
            }),
            reduce: (accumulated: any, props: any) => {
                if (props.maxSeverity > accumulated.maxSeverity) {
                    accumulated.maxSeverity = props.maxSeverity;
                }
                accumulated.count += props.count;
            }
        });

        const points: Array<GeoJSON.Feature<GeoJSON.Point, any>> = incidents.map(inc => ({
            type: "Feature",
            properties: {
                incident: inc
            },
            geometry: {
                type: "Point",
                coordinates: inc.position.coordinates
            }
        }));

        index.load(points);
        return index;
    }, [incidents, clusteringEnabled]);

    const getTimeRange = useCallback((tr: string) => {
        const now = Date.now();
        const offsets: Record<string, number> = {
            "12h": 12 * 3600000,
            "24h": 24 * 3600000,
            "1w": 7 * 24 * 3600000,
            "1m": 30 * 24 * 3600000
        };
        const ms = offsets[tr] || offsets["24h"];
        return {startTime: new Date(now - ms).toISOString(), endTime: new Date(now).toISOString()};
    }, []);

    const handleFilterChange = useCallback((_: number) => (newParams: FilterQueryParams) => {
        setFilterParams(prev => {
            const merged = {...prev, ...newParams};
            Object.keys(merged).forEach(k => (merged as any)[k] === undefined && delete (merged as any)[k]);
            return merged;
        });
    }, []);

    // Load Google Maps
    useEffect(() => {
        if ((window as any).google?.maps?.Map) {
            // Already loaded, add small delay to ensure complete initialization
            setTimeout(() => setGoogleLoaded(true), 100);
            return;
        }

        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&v=3.64&libraries=drawing,places,marker`;
        script.async = true;
        script.onload = () => {
            // Wait a bit after load to ensure Google Maps is fully initialized
            setTimeout(() => {
                if ((window as any).google?.maps?.Map) {
                    setGoogleLoaded(true);
                }
            }, 200);
        };
        script.onerror = () => {
            console.error("Failed to load Google Maps");
        };
        document.head.appendChild(script);
    }, []);

    // Initialize WebGL
    const initWebGL = useCallback(() => {
        if (!canvasRef.current) return;

        const canvas = canvasRef.current;
        const gl = canvas.getContext("webgl", {alpha: true, premultipliedAlpha: false});
        if (!gl) {
            console.error("WebGL not supported");
            return;
        }

        glRef.current = gl;

        // Vertex shader
        const vs = `
            attribute vec2 a_position;
            attribute vec3 a_color;
            attribute float a_offset;
            attribute float a_severity;
            attribute float a_renderMode;
            attribute float a_highlighted;
            attribute float a_count; // Incident count (1 for individual, >1 for cluster)
            uniform float u_zoom; // Current map zoom level
            varying vec3 v_color;
            varying float v_offset;
            varying float v_highlighted;
            varying float v_count;
            void main() {
                // Scale size based on count (clusters grow with count, always larger than individuals)
                float baseSize = a_highlighted > 0.5 ? 60.0 : 40.0;
                // Individuals: 1.0 (baseline)
                // Clusters: 1.1 + (sqrt(count) - sqrt(2)) * 0.3
                // This ensures smallest cluster (2) = 1.1x, then scales up from there
                float capped_count = min(a_count, 100.0);
                float scaleFactor = a_count > 1.0 ? 1.1 + (sqrt(capped_count) - sqrt(2.0)) * 0.3 : 1.0;

                // Subtle zoom scaling: scale up point size slightly as we zoom in
                // At zoom 5, scale = 1.0 (baseline)
                // Each zoom level multiplies size by 1.05 (5% increase)
                float zoomScale = pow(1.05, u_zoom - 5.0);

                gl_PointSize = min(baseSize * scaleFactor * zoomScale, 120.0);

                // Z-ordering: pulses (mode 0) behind all cores, cores ordered by count
                // Higher z = farther from camera = rendered behind
                // Pulses: z = 0.5 (far back, behind everything)
                // Cores: smaller clusters on top (fewer incidents = more negative z = closer)
                // Highlighted cores: subtract additional 0.5 to bring them to front
                float depth;
                if (a_renderMode < 0.5) {
                    // Pulse mode - push far back (well behind all cores)
                    depth = 0.9;
                } else {
                    // Highlighted items always render on top (most negative depth)
                    if (a_highlighted > 0.5) {
                        depth = -1.0; // Always on top
                    } else {
                        // Core mode - order by count first, then by severity
                        // Primary: smaller clusters on top (fewer incidents = more negative)
                        // Secondary: higher severity on top within same cluster size
                        // Count component: -0.3 to 0.0 (based on count)
                        // Severity component: -0.05 to 0.0 (based on severity 0-5)
                        float countDepth = -0.3 + (min(a_count, 100.0) / 100.0);
                        float severityDepth = -0.05 + (a_severity / 100.0);
                        depth = countDepth + severityDepth;
                    }
                }
                gl_Position = vec4(a_position, depth, 1.0);
                v_color = a_color;
                v_offset = a_offset;
                v_highlighted = a_highlighted;
                v_count = a_count;
            }
        `;

        // Fragment shader - pulsing circle with fading ring
        const fs = `
            precision highp float;
            varying vec3 v_color;
            varying float v_offset;
            varying float v_highlighted;
            varying float v_count;
            uniform float u_time;
            uniform int u_renderMode; // 0 = pulse only, 1 = core only

            void main() {
                vec2 coord = gl_PointCoord - vec2(0.5);
                float dist = length(coord) * 2.0; // Scale to 0-1 range

                // Pulsing animation - only outward expansion (1.5 second period)
                // Add v_offset to stagger the timing per point
                float pulse = fract((u_time + v_offset) / 1.5); // Creates sawtooth wave 0 to 1

                // Core circle (solid)
                float coreRadius = 0.35;
                float aaWidth = 0.02; // Anti-aliasing edge width

                if (u_renderMode == 1) {
                    // Render only the core with border
                    float borderWidth = ${BORDER_WIDTH.toFixed(2)};
                    float borderLightness = ${BORDER_LIGHTNESS.toFixed(2)};
                    float centerRadius = 0.2; // Size of the lighter center circle

                    vec3 coreColor = v_color;
                    if (v_highlighted > 0.5) {
                        coreColor = min(v_color * 1.15, vec3(1.0));
                    }

                    // Lighter center color
                    vec3 lighterColor = mix(vec3(1.0), coreColor, 0.5);

                    // Border color
                    vec3 borderColor;
                    if (v_highlighted > 0.5) {
                        borderColor = vec3(1.0, 1.0, 1.0); // White highlight border
                    } else {
                        borderColor = mix(v_color, vec3(1.0), borderLightness);
                    }

                    // Calculate smooth transitions
                    // Center to outer transition
                    float centerBlend = smoothstep(centerRadius - aaWidth, centerRadius + aaWidth, dist);
                    vec3 innerColor = mix(lighterColor, coreColor, centerBlend);

                    // Outer to border transition
                    float borderStart = coreRadius - borderWidth;
                    float borderBlend = smoothstep(borderStart - aaWidth, borderStart + aaWidth, dist);
                    vec3 finalColor = mix(innerColor, borderColor, borderBlend);

                    // Outer edge anti-aliasing (fade to transparent)
                    float edgeAlpha = 1.0 - smoothstep(coreRadius - aaWidth, coreRadius + aaWidth, dist);

                    if (edgeAlpha > 0.0) {
                        gl_FragColor = vec4(finalColor, edgeAlpha);
                    } else {
                        discard;
                    }
                } else {
                    // Render only the pulse ring - emerges from inside core
                    float maxExpansion = 0.5;
                    float ringWidth = 0.15;

                    // Ring outer edge starts at core radius and expands
                    float ringOuter = coreRadius + (pulse * maxExpansion);
                    float ringInner = ringOuter - ringWidth;

                    // Smooth inner and outer edges of pulse ring
                    float innerEdge = smoothstep(ringInner - aaWidth, ringInner + aaWidth, dist);
                    float outerEdge = 1.0 - smoothstep(ringOuter - aaWidth, ringOuter + aaWidth, dist);
                    float ringMask = innerEdge * outerEdge;

                    if (ringMask > 0.0) {
                        float alpha = (1.0 - pulse) * 0.9 * ringMask;
                        gl_FragColor = vec4(v_color, alpha);
                    } else {
                        discard;
                    }
                }
            }
        `;

        // Compile shaders
        const compileShader = (type: number, source: string) => {
            const shader = gl.createShader(type);
            if (!shader) return null;
            gl.shaderSource(shader, source);
            gl.compileShader(shader);
            if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
                console.error("Shader compile error:", gl.getShaderInfoLog(shader));
                gl.deleteShader(shader);
                return null;
            }
            return shader;
        };

        const vertexShader = compileShader(gl.VERTEX_SHADER, vs);
        const fragmentShader = compileShader(gl.FRAGMENT_SHADER, fs);
        if (!vertexShader || !fragmentShader) return;

        // Create program
        const program = gl.createProgram();
        if (!program) return;
        gl.attachShader(program, vertexShader);
        gl.attachShader(program, fragmentShader);
        gl.linkProgram(program);

        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
            console.error("Program link error:", gl.getProgramInfoLog(program));
            return;
        }

        programRef.current = program;

        // Enable depth testing for z-ordering
        gl.enable(gl.DEPTH_TEST);
        gl.depthFunc(gl.LEQUAL);

        // Setup blending
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    }, []);

    // Draw function
    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        const gl = glRef.current;
        const program = programRef.current;
        const map = mapRef.current;
        const overlay = overlayRef.current;

        if (!canvas || !gl || !program || !map || !overlay) return;

        // Extra safety check - wrap in try-catch to prevent Google Maps internal errors
        try {
            const projection = (overlay as any).getProjectionSafe();
            if (!projection) return;

            // Size canvas buffer to match map size
            const mapDiv = map.getDiv();
            if (!mapDiv) return;

            const dpr = window.devicePixelRatio || 1;
            const width = mapDiv.offsetWidth;
            const height = mapDiv.offsetHeight;

            // Don't render if dimensions are invalid
            if (!width || !height || width <= 0 || height <= 0) return;

            // Set both CSS and buffer dimensions
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;
            canvas.width = width * dpr;
            canvas.height = height * dpr;

            gl.viewport(0, 0, canvas.width, canvas.height);
            gl.clearColor(0, 0, 0, 0);
            gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
            gl.useProgram(program);

            // Set uniforms
            const uTimeLoc = gl.getUniformLocation(program, "u_time");
            const uRenderModeLoc = gl.getUniformLocation(program, "u_renderMode");
            const uZoomLoc = gl.getUniformLocation(program, "u_zoom");
            gl.uniform1f(uTimeLoc, performance.now() / 1000);
            const currentZoom = map.getZoom() || 5;
            gl.uniform1f(uZoomLoc, currentZoom);

            const positions: number[] = [];
            const colors: number[] = [];
            const offsets: number[] = [];
            const severities: number[] = [];
            const renderModes: number[] = [];
            const highlighted: number[] = [];
            const counts: number[] = [];

            // Simple hash function to generate consistent offset from incident ID
            const hashOffset = (id: string): number => {
                if (!STAGGERED_PULSES) return 0;
                let hash = 0;
                for (let i = 0; i < id.length; i++) {
                    hash = ((hash << 5) - hash) + id.charCodeAt(i);
                    hash = hash & hash;
                }
                return (Math.abs(hash) % 3000) / 1000; // 0-3 seconds
            };

            // Map severity to numeric value (higher = more severe = rendered on top)
            const getSeverityDepth = (severity: string): number => {
                const s = severity?.toLowerCase() || "";
                if (s === "extreme") return 4;
                if (s === "major") return 3;
                if (s === "moderate") return 2;
                if (s === "limited") return 1;
                if (s === "insignificant") return 0;
                return 0; // unknown
            };

            // Get points to render (either clusters or individual incidents)
            const pointsToRender: Array<{
                coords: [number, number];
                color: [number, number, number];
                severity: number;
                count: number;
                id: string;
                isCluster: boolean;
                incident?: Incident;
            }> = [];

            if (clusteringEnabled && supercluster) {
                // Get current zoom and bounds
                const zoom = Math.floor(map.getZoom() || 0);
                const bounds = map.getBounds();
                if (bounds) {
                    const ne = bounds.getNorthEast();
                    const sw = bounds.getSouthWest();
                    const bbox: [number, number, number, number] = [
                        sw.lng(), sw.lat(), ne.lng(), ne.lat()
                    ];

                    const clusters = supercluster.getClusters(bbox, zoom);

                    for (const cluster of clusters) {
                        const [lng, lat] = cluster.geometry.coordinates;
                        const props: any = cluster.properties;

                        if (props.cluster) {
                            const count = props.point_count || 0;
                            const maxSeverity = props.maxSeverity ?? 1;
                            const color = getColorFromPriority(maxSeverity);

                            pointsToRender.push({
                                coords: [lng, lat],
                                color,
                                severity: maxSeverity,
                                count,
                                id: `cluster_${props.cluster_id}`,
                                isCluster: true
                            });
                        } else {
                            // This is a single point
                            const inc = props.incident;
                            const color = getIncidentColor(inc);

                            pointsToRender.push({
                                coords: [lng, lat],
                                color,
                                severity: getSeverityDepth(inc.severity),
                                count: 1,
                                id: inc.id,
                                isCluster: false,
                                incident: inc
                            });
                        }
                    }
                }
            } else {
                // No clustering - render all incidents
                for (const inc of incidents) {
                    const [lng, lat] = inc.position.coordinates;
                    if (typeof lat !== "number" || typeof lng !== "number") continue;

                    const color = getIncidentColor(inc);
                    pointsToRender.push({
                        coords: [lng, lat],
                        color,
                        severity: getSeverityDepth(inc.severity),
                        count: 1,
                        id: inc.id,
                        isCluster: false,
                        incident: inc
                    });
                }
            }

            // Now render all points
            const newRenderedPoints: typeof renderedPointsRef.current = [];

            for (const point of pointsToRender) {
                const [lng, lat] = point.coords;
                const latLng = new google.maps.LatLng(lat, lng);

                // Use container pixel which is relative to map container
                const pixel = projection.fromLatLngToContainerPixel(latLng);
                if (!pixel || typeof pixel.x !== "number" || typeof pixel.y !== "number") continue;

                // Cache pixel position for hit-testing (avoids re-projecting on every mousemove)
                let pointIncidents: Incident[];
                if (point.isCluster && clusteringEnabled && supercluster) {
                    const clusterId = parseInt(point.id.replace("cluster_", ""), 10);
                    pointIncidents = supercluster.getLeaves(clusterId, Infinity).map((l: any) => l.properties.incident);
                } else {
                    pointIncidents = point.incident ? [point.incident] : [];
                }
                newRenderedPoints.push({
                    x: pixel.x,
                    y: pixel.y,
                    id: point.id,
                    isCluster: point.isCluster,
                    incidents: pointIncidents
                });

                // Normalize to clip space (-1 to 1)
                const normX = (pixel.x / width) * 2 - 1;
                const normY = -(pixel.y / height) * 2 + 1;

                positions.push(normX, normY);

                const [r, g, b] = point.color;
                colors.push(r / 255, g / 255, b / 255);

                // Add time offset for staggered pulses
                offsets.push(hashOffset(point.id));

                // Add severity depth for z-ordering
                severities.push(point.severity);

                // Add highlight flag - check if this point is hovered
                const isHighlighted = hoveredPointIdsRef.current.has(point.id);
                highlighted.push(isHighlighted ? 1 : 0);

                // Add count
                counts.push(point.count);
            }

            // Commit pixel cache for hit-testing
            renderedPointsRef.current = newRenderedPoints;

            if (positions.length === 0) return;

            // Upload data
            const posBuffer = gl.createBuffer();
            gl.bindBuffer(gl.ARRAY_BUFFER, posBuffer);
            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);
            const posLoc = gl.getAttribLocation(program, "a_position");
            gl.enableVertexAttribArray(posLoc);
            gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

            const colorBuffer = gl.createBuffer();
            gl.bindBuffer(gl.ARRAY_BUFFER, colorBuffer);
            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(colors), gl.STATIC_DRAW);
            const colorLoc = gl.getAttribLocation(program, "a_color");
            gl.enableVertexAttribArray(colorLoc);
            gl.vertexAttribPointer(colorLoc, 3, gl.FLOAT, false, 0, 0);

            const offsetBuffer = gl.createBuffer();
            gl.bindBuffer(gl.ARRAY_BUFFER, offsetBuffer);
            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(offsets), gl.STATIC_DRAW);
            const offsetLoc = gl.getAttribLocation(program, "a_offset");
            gl.enableVertexAttribArray(offsetLoc);
            gl.vertexAttribPointer(offsetLoc, 1, gl.FLOAT, false, 0, 0);

            const severityBuffer = gl.createBuffer();
            gl.bindBuffer(gl.ARRAY_BUFFER, severityBuffer);
            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(severities), gl.STATIC_DRAW);
            const severityLoc = gl.getAttribLocation(program, "a_severity");
            gl.enableVertexAttribArray(severityLoc);
            gl.vertexAttribPointer(severityLoc, 1, gl.FLOAT, false, 0, 0);

            const highlightedBuffer = gl.createBuffer();
            gl.bindBuffer(gl.ARRAY_BUFFER, highlightedBuffer);
            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(highlighted), gl.STATIC_DRAW);
            const highlightedLoc = gl.getAttribLocation(program, "a_highlighted");
            gl.enableVertexAttribArray(highlightedLoc);
            gl.vertexAttribPointer(highlightedLoc, 1, gl.FLOAT, false, 0, 0);

            const countBuffer = gl.createBuffer();
            gl.bindBuffer(gl.ARRAY_BUFFER, countBuffer);
            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(counts), gl.STATIC_DRAW);
            const countLoc = gl.getAttribLocation(program, "a_count");
            gl.enableVertexAttribArray(countLoc);
            gl.vertexAttribPointer(countLoc, 1, gl.FLOAT, false, 0, 0);

            // Create render mode buffer and location (used by both passes)
            const renderModeBuffer = gl.createBuffer();
            const renderModeLoc = gl.getAttribLocation(program, "a_renderMode");
            gl.enableVertexAttribArray(renderModeLoc);

            // First pass: Draw pulse rings (behind all cores at z = -0.5)
            // Only render if pulses are enabled
            if (pulsesEnabled) {
                // Set renderMode attribute to 0 for all vertices
                const pulseModes = new Array(positions.length / 2).fill(0);
                gl.bindBuffer(gl.ARRAY_BUFFER, renderModeBuffer);
                gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(pulseModes), gl.STATIC_DRAW);
                gl.vertexAttribPointer(renderModeLoc, 1, gl.FLOAT, false, 0, 0);

                gl.uniform1i(uRenderModeLoc, 0);
                gl.drawArrays(gl.POINTS, 0, positions.length / 2);
            }

            // Second pass: Draw solid cores (ordered by severity z = 0.0 to 0.4)
            // Set renderMode attribute to 1 for all vertices
            const coreModes = new Array(positions.length / 2).fill(1);
            gl.bindBuffer(gl.ARRAY_BUFFER, renderModeBuffer);
            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(coreModes), gl.STATIC_DRAW);
            gl.vertexAttribPointer(renderModeLoc, 1, gl.FLOAT, false, 0, 0);

            // Re-bind highlighted buffer to ensure it's still active
            gl.bindBuffer(gl.ARRAY_BUFFER, highlightedBuffer);
            gl.vertexAttribPointer(highlightedLoc, 1, gl.FLOAT, false, 0, 0);

            gl.uniform1i(uRenderModeLoc, 1);
            gl.drawArrays(gl.POINTS, 0, positions.length / 2);
        } catch (error) {
            // Silently ignore Google Maps internal errors during rendering
            // These typically happen during map transitions and are safe to ignore
        }
    }, [incidents, pulsesEnabled, clusteringEnabled, supercluster]);

    // Keep latest draw function in ref so map init effect can always call it
    const drawRef = useRef(draw);
    useEffect(() => {
        drawRef.current = draw;
    }, [draw]);

    // Init map and canvas
    useEffect(() => {
        if (!googleLoaded || !mapDivRef.current) return;

        const center = incidents.length > 0
            ? {
                lat: incidents.reduce((s, i) => s + i.position.coordinates[1], 0) / incidents.length,
                lng: incidents.reduce((s, i) => s + i.position.coordinates[0], 0) / incidents.length,
            }
            : {lat: 20, lng: 0};

        const map = new google.maps.Map(mapDivRef.current, {
            center,
            zoom: incidents.length === 0 ? 2 : 5,
            minZoom: 4,
            maxZoom: 20,
            gestureHandling: "greedy",
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            mapId: process.env.REACT_APP_GOOGLE_MAP_ID, // Use vector map
            backgroundColor: "#6cd2e7", // Match Google Maps ocean color
            // Optimize for smoother panning
            isFractionalZoomEnabled: false, // Prevents loading tiles at fractional zoom levels
            // Restrict to world bounds to reduce unnecessary tile loading
            restriction: {
                latLngBounds: {
                    north: 85,
                    south: -85,
                    west: -180,
                    east: 180,
                },
                strictBounds: false,
            },
        });

        mapRef.current = map;

        // Add right-click listener for coordinate display
        map.addListener("rightclick", (e: google.maps.MapMouseEvent) => {
            if (e.latLng && e.domEvent && e.domEvent instanceof MouseEvent) {
                setRightClickPosition({
                    lat: e.latLng.lat(),
                    lng: e.latLng.lng(),
                    x: e.domEvent.clientX,
                    y: e.domEvent.clientY
                });
            }
        });

        // Initialize Google Places Autocomplete for search
        if (searchInputRef.current && window.google?.maps?.places) {
            const autocomplete = new window.google.maps.places.Autocomplete(searchInputRef.current, {
                fields: ["geometry", "name", "formatted_address"],
            });

            autocomplete.addListener("place_changed", () => {
                const place = autocomplete.getPlace();
                if (place.geometry?.location) {
                    map.setCenter(place.geometry.location);
                    map.setZoom(12);
                }
            });
        }

        // Create canvas overlay
        const canvas = document.createElement("canvas");
        canvas.style.position = "absolute";
        canvas.style.left = "0";
        canvas.style.top = "0";
        canvas.style.pointerEvents = "none";
        canvasRef.current = canvas;

        // Append canvas to map div (not overlay panes)
        const mapDiv = map.getDiv();
        mapDiv.appendChild(canvas);

        // Create OverlayView just for projection API
        class CanvasOverlay extends google.maps.OverlayView {
            private isReady = false;

            onAdd() {
                // Mark as ready once added to map
                this.isReady = true;
            }

            draw() {
                // This is called by Google Maps on pan/zoom
                // We handle continuous animation separately
            }

            onRemove() {
                this.isReady = false;
            }

            getProjectionSafe() {
                return this.isReady ? this.getProjection() : null;
            }
        }

        // Init WebGL
        initWebGL();

        const overlay = new CanvasOverlay();
        overlay.setMap(map);
        overlayRef.current = overlay;

        // Continuous animation loop for pulsing effect
        let isAnimating = false;
        const animate = () => {
            if (isAnimating) {
                drawRef.current(); // Use ref to always get latest draw function
                animationFrameRef.current = requestAnimationFrame(animate);
            }
        };

        // Add listeners for map movement to trigger immediate redraws
        const mapListeners: google.maps.MapsEventListener[] = [];

        // Wait for overlay to be ready before starting animation
        // Add additional delay to ensure all Google Maps internals are initialized
        google.maps.event.addListenerOnce(map, "idle", () => {
            // Small delay to ensure everything is fully ready
            setTimeout(() => {
                isAnimating = true;
                animate();

                // Sync overlay with map movements for responsive feel
                mapListeners.push(
                    google.maps.event.addListener(map, "bounds_changed", () => {
                        drawRef.current(); // Use ref to always get latest draw function
                    })
                );
                mapListeners.push(
                    google.maps.event.addListener(map, "zoom_changed", () => {
                        drawRef.current(); // Use ref to always get latest draw function
                    })
                );
                mapListeners.push(
                    google.maps.event.addListener(map, "center_changed", () => {
                        drawRef.current(); // Use ref to always get latest draw function
                    })
                );
            }, 100);
        });

        return () => {
            isAnimating = false;
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
            // Remove map event listeners
            mapListeners.forEach(listener => {
                google.maps.event.removeListener(listener);
            });
            overlay.setMap(null);
            canvas.remove();
        };
    }, [googleLoaded, initWebGL]);

    // Redraw when incidents change
    useEffect(() => {
        if (overlayRef.current && mapRef.current && glRef.current && programRef.current) {
            draw();
        }
    }, [incidents, draw]);

    // Update map type when changed
    useEffect(() => {
        if (!mapRef.current) return;
        mapRef.current.setMapTypeId(mapType);
    }, [mapType]);

    // Save mapType to localStorage when it changes
    useEffect(() => {
        try {
            localStorage.setItem("incidentMapType", mapType);
        } catch (error) {
            console.error("Failed to save map type to localStorage:", error);
        }
    }, [mapType]);

    // Flush batched WS incident creates/updates in a single setState
    const scheduleIncidentFlush = useCallback(() => {
        if (incidentFlushTimerRef.current !== null) return;
        incidentFlushTimerRef.current = setTimeout(() => {
            incidentFlushTimerRef.current = null;
            const {created, updated} = pendingIncidentUpdatesRef.current;
            pendingIncidentUpdatesRef.current = {created: [], updated: []};
            if (created.length === 0 && updated.length === 0) return;
            setIncidents(prev => {
                const next = [...prev, ...created];
                for (const inc of updated) {
                    const idx = next.findIndex(i => i.id === inc.id);
                    if (idx >= 0) next[idx] = inc;
                    else next.push(inc);
                }
                return next;
            });
        }, 200);
    }, []);

    // WebSocket connection for real-time incidents
    useEffect(() => {
        if (!googleLoaded) return;

        // Define WebSocket message handlers
        const handlers: MessageHandlers = {
            snapshot: (data: any) => {
                const newIncidents = (data.incidents || []).filter((i: any) => i.position?.coordinates);
                setIncidents(newIncidents);
                setStoreLoading(false);
                setLoading(false);
            },
            loading: (_data: any) => {
                setStoreLoading(true);
                setLoading(false);
            },
            created: (data: any) => {
                if (data.incident?.position?.coordinates) {
                    pendingIncidentUpdatesRef.current.created.push(data.incident);
                    scheduleIncidentFlush();
                }
            },
            updated: (data: any) => {
                if (data.incident?.position?.coordinates) {
                    pendingIncidentUpdatesRef.current.updated.push(data.incident);
                    scheduleIncidentFlush();
                }
            },
            incidentsPreview: (data: any) => {
                // Handle preview data for popup
                if (pendingIncidentDetailsRef.current) {
                    const { incidentIDs, callback } = pendingIncidentDetailsRef.current;
                    pendingIncidentDetailsRef.current = null;
                    const incidents: any[] = data.incidents || [];
                    // Restore severity-sorted order from the requested ID list
                    const idOrder = new Map(incidentIDs.map((id, i) => [id, i]));
                    incidents.sort((a, b) => (idOrder.get(a.id) ?? 0) - (idOrder.get(b.id) ?? 0));
                    callback(incidents);
                }
            },
            incidentDetails: (data: any) => {
                // Handle full incident details for modal
                if (data.incident) {
                    setSelectedIncident(data.incident);
                    setModalOpen(true);
                    setPopupVisible(false);
                    setTooltipVisible(false);
                }
            },
            error: (data: any) => {
                console.error("WebSocket error:", data.message);
                setLoading(false);
            }
        };

        // Create WebSocket manager
        const wsManager = platformClient.ws(
            "/ws/incidents",
            handlers,
            (state: ConnectionState) => {
                setWsConnectionState(state);

                // Send filters when connection is established
                if (state === ConnectionState.CONNECTED) {
                    const filters = latestFiltersRef.current;
                    // Check if we have a feed selected
                    if (filters.feedIDs &&
                        Array.isArray(filters.feedIDs) &&
                        filters.feedIDs.length > 0) {

                        const {timeRange, categoryTypeIDs, tags, excludedTags, tagMode, ...rest} = filters;
                        const {startTime, endTime} = getTimeRange(timeRange as string || "24h");
                        wsManager.send("setFilters", {
                            filters: {
                                ...rest,
                                typeIDs: categoryTypeIDs,
                                tags: {
                                    include: tags || [],
                                    exclude: excludedTags || [],
                                    includeMatchAll: tagMode === "all"
                                },
                                dateRange: {
                                    start: startTime,
                                    end: endTime
                                }
                            }
                        });
                    } else {
                        setIncidents([]);
                        setLoading(false);
                    }
                }
            }
        );

        wsManagerRef.current = wsManager;
        setLoading(true);

        // Connect asynchronously (errors handled by WebSocketManager)
        wsManager.connect().catch((error) => {
            console.error("Failed to connect to WebSocket:", error);
            setLoading(false);
        });

        return () => {
            wsManager.disconnect();
            wsManagerRef.current = null;
        };
    }, [googleLoaded, scheduleIncidentFlush, platformClient]);

    // Send filter updates when filters change
    useEffect(() => {
        if (!wsManagerRef.current || !wsManagerRef.current.isConnected()) return;

        // Don't send if no feed is selected
        if (!debouncedFilterParams.feedIDs ||
            !Array.isArray(debouncedFilterParams.feedIDs) ||
            debouncedFilterParams.feedIDs.length === 0) {
            setIncidents([]);
            return;
        }

        // Send filters - map frontend field names to backend field names
        const {timeRange, categoryTypeIDs, tags, excludedTags, tagMode, ...rest} = debouncedFilterParams;
        const {startTime, endTime} = getTimeRange(timeRange as string || "24h");
        wsManagerRef.current.send("setFilters", {
            filters: {
                ...rest,
                typeIDs: categoryTypeIDs, // Backend expects "typeIDs" not "categoryTypeIDs"
                tags: {
                    include: tags || [],
                    exclude: excludedTags || [],
                    includeMatchAll: tagMode === "all"
                },
                dateRange: {
                    start: startTime,
                    end: endTime
                }
            }
        });
    }, [debouncedFilterParams, getTimeRange]);

    // URL sync and localStorage save (only save options)
    useEffect(() => {
        const p = new URLSearchParams();
        if (Object.keys(filterParams).length) {
            p.set("filters", JSON.stringify(filterParams));
        }
        setSearchParams(p, {replace: true});

        // Save options and feed selection to localStorage
        try {
            const optionsToSave: any = {};
            if (filterParams.disableAnimations !== undefined) {
                optionsToSave.disableAnimations = filterParams.disableAnimations;
            }
            if (filterParams.enableClustering !== undefined) {
                optionsToSave.enableClustering = filterParams.enableClustering;
            }
            localStorage.setItem("incidentMapOptions", JSON.stringify(optionsToSave));
            localStorage.setItem("incidentFeedSelection", JSON.stringify({
                feedIDs: filterParams.feedIDs ?? [],
                feedNames: filterParams.feedNames ?? [],
                feedType: filterParams.feedType ?? "",
            }));
        } catch (error) {
            console.error("Failed to save options to localStorage:", error);
        }
    }, [filterParams, setSearchParams]);

    // Click and hover handling on canvas
    useEffect(() => {
        const map = mapRef.current;
        const overlay = overlayRef.current;
        const canvas = canvasRef.current;
        if (!map || !overlay || !canvas) return;

        const mapDiv = map.getDiv();

        // Hit-test against pixel cache built by draw() — no projection calls needed.
        // For clusters: returns the closest cluster's leaf incidents (already grouped).
        // For non-clustered: returns all incidents within radius across all nearby points.
        const getIncidentsAtPoint = (clientX: number, clientY: number): Incident[] => {
            if (!mapDiv) return [];
            const rect = mapDiv.getBoundingClientRect();
            const x = clientX - rect.left;
            const y = clientY - rect.top;
            const clickRadius = 30;

            const getSeverityRank = (incident: Incident): number => {
                // Use getIncidentPriority (higher = more severe) and invert so lower rank = higher severity
                return -getIncidentPriority(incident);
            };

            // Find the closest rendered point within radius
            let closestDist = Infinity;
            let closestPoint: typeof renderedPointsRef.current[0] | null = null;

            for (const pt of renderedPointsRef.current) {
                const dist = Math.hypot(pt.x - x, pt.y - y);
                if (dist <= clickRadius && dist < closestDist) {
                    closestDist = dist;
                    closestPoint = pt;
                }
            }

            if (!closestPoint) return [];

            // If it's a cluster, return all its leaf incidents (already collected in cache)
            if (closestPoint.isCluster) {
                const result = [...closestPoint.incidents];
                result.sort((a, b) => getSeverityRank(a) - getSeverityRank(b));
                return result;
            }

            // Non-clustered: collect incidents from all nearby points within radius
            const nearby: Array<{incident: Incident; dist: number}> = [];
            for (const pt of renderedPointsRef.current) {
                const dist = Math.hypot(pt.x - x, pt.y - y);
                if (dist <= clickRadius && pt.incidents.length > 0) {
                    nearby.push({incident: pt.incidents[0], dist});
                }
            }
            nearby.sort((a, b) => {
                const sd = getSeverityRank(a.incident) - getSeverityRank(b.incident);
                return sd !== 0 ? sd : a.dist - b.dist;
            });
            return nearby.map(n => n.incident);
        };

        let mouseDownPos = {x: 0, y: 0};

        let currentCursor = "grab";

        // Force cursor style aggressively - Google Maps tries to override it
        const forceCursor = (cursor: string) => {
            mapDiv.style.setProperty("cursor", cursor, "important");
            const children = mapDiv.querySelectorAll("div");
            children.forEach(child => {
                (child as HTMLElement).style.setProperty("cursor", cursor, "important");
            });
        };

        // Watch for Google Maps trying to change the cursor and override it
        const cursorObserver = new MutationObserver(() => {
            forceCursor(currentCursor);
        });
        cursorObserver.observe(mapDiv, {
            attributes: true,
            attributeFilter: ["style"],
            subtree: true
        });

        // Set initial cursor
        forceCursor(currentCursor);

        let mouseMoveRafId: number | null = null;
        let pendingMouseX = 0;
        let pendingMouseY = 0;

        const processMouseMove = () => {
            mouseMoveRafId = null;
            const nearbyIncidents = getIncidentsAtPoint(pendingMouseX, pendingMouseY);

            // Derive hovered point IDs — only highlight the single closest point
            const hoveredPointIds = new Set<string>();
            if (!mapDiv) return;
            const rect = mapDiv.getBoundingClientRect();
            const x = pendingMouseX - rect.left;
            const y = pendingMouseY - rect.top;
            const clickRadius = 30;
            let closestDist = Infinity;
            let closestId: string | null = null;
            for (const pt of renderedPointsRef.current) {
                const dist = Math.hypot(pt.x - x, pt.y - y);
                if (dist <= clickRadius && dist < closestDist) {
                    closestDist = dist;
                    closestId = pt.id;
                }
            }
            if (closestId) hoveredPointIds.add(closestId);

            // Update cursor
            const newCursor = nearbyIncidents.length > 0 ? "pointer" : "grab";
            if (newCursor !== currentCursor) {
                currentCursor = newCursor;
                forceCursor(currentCursor);
            }

            // Update tooltip
            if (nearbyIncidents.length > 0) {
                setTooltipVisible(true);
                setTooltipPosition({x: pendingMouseX, y: pendingMouseY});
                setTooltipCount(nearbyIncidents.length);
                setTooltipIncidents(nearbyIncidents);
            } else {
                setTooltipVisible(false);
            }

            // Update hovered points for highlighting — only redraw if changed
            const currentIds = hoveredPointIdsRef.current;
            const hasChanged = currentIds.size !== hoveredPointIds.size ||
                [...currentIds].some(id => !hoveredPointIds.has(id));
            if (hasChanged) {
                hoveredPointIdsRef.current = hoveredPointIds;
                if (overlayRef.current && mapRef.current && glRef.current && programRef.current) {
                    draw();
                }
            }

            hoveredIncidentsRef.current = new Set(nearbyIncidents.map(inc => inc.id));
        };

        const handleMouseMove = (e: MouseEvent) => {
            // Track drag
            const dx = e.clientX - mouseDownPos.x;
            const dy = e.clientY - mouseDownPos.y;
            if (Math.hypot(dx, dy) > 5) {
                isDraggingRef.current = true;
            }

            // Throttle to one process per animation frame
            pendingMouseX = e.clientX;
            pendingMouseY = e.clientY;
            if (mouseMoveRafId === null) {
                mouseMoveRafId = requestAnimationFrame(processMouseMove);
            }
        };

        const handleWheel = (e: WheelEvent) => {
            // Always let wheel events pass through to map
            // Don't do anything, just let the map handle it
        };

        const handleMouseDown = (e: MouseEvent) => {
            mouseDownPos = {x: e.clientX, y: e.clientY};
            isDraggingRef.current = false;
        };

        const handleClick = (e: MouseEvent) => {
            if (isDraggingRef.current) {
                isDraggingRef.current = false;
                return;
            }

            const nearbyIncidents = getIncidentsAtPoint(e.clientX, e.clientY);

            if (nearbyIncidents.length === 0) {
                // No incidents nearby, close popup if open
                setPopupVisible(false);
            } else if (nearbyIncidents.length === 1) {
                // Only one incident, request full details via WebSocket
                e.stopPropagation();
                setPopupVisible(false);
                setTooltipVisible(false);

                if (wsManagerRef.current && wsManagerRef.current.isConnected()) {
                    wsManagerRef.current.send("getIncident", {
                        incidentID: nearbyIncidents[0].id
                    });
                }
            } else {
                // Multiple incidents, request preview data via WebSocket
                e.stopPropagation();
                const clickPosition = {
                    x: e.clientX,
                    y: e.clientY
                };

                if (wsManagerRef.current && wsManagerRef.current.isConnected()) {
                    const allIDs = nearbyIncidents.map(inc => inc.id);
                    popupAllIDsRef.current = allIDs;
                    const firstPageIDs = allIDs.slice(0, POPUP_PAGE_SIZE);

                    pendingIncidentDetailsRef.current = {
                        incidentIDs: firstPageIDs,
                        callback: (previewIncidents: any[]) => {
                            popupClampedRef.current = false;
                            setPopupPosition(clickPosition);
                            setPopupIncidents(previewIncidents);
                            setPopupPage(1);
                            setPopupTotalCount(allIDs.length);
                            setPopupVisible(true);
                        }
                    };

                    wsManagerRef.current.send("getIncidentsPreview", {
                        incidentIDs: firstPageIDs
                    });
                }
            }
        };

        mapDiv.addEventListener("mousemove", handleMouseMove);
        mapDiv.addEventListener("mousedown", handleMouseDown);
        mapDiv.addEventListener("click", handleClick, true); // Use capture to get clicks before map
        mapDiv.addEventListener("wheel", handleWheel, {passive: true});

        return () => {
            cursorObserver.disconnect();
            if (mouseMoveRafId !== null) cancelAnimationFrame(mouseMoveRafId);
            mapDiv.removeEventListener("mousemove", handleMouseMove);
            mapDiv.removeEventListener("mousedown", handleMouseDown);
            mapDiv.removeEventListener("click", handleClick, true);
            mapDiv.removeEventListener("wheel", handleWheel);
        };
    }, [draw, clusteringEnabled, supercluster]);

    // Close popup when clicking outside
    useEffect(() => {
        if (!popupVisible) return;

        const handleClickOutside = (e: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
                setPopupVisible(false);
            }
        };

        // Add listener with a small delay to avoid immediate close from the opening click
        const timeoutId = setTimeout(() => {
            document.addEventListener("mousedown", handleClickOutside);
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [popupVisible]);

    // Close coordinate popup when clicking outside
    useEffect(() => {
        if (!rightClickPosition) return;

        const handleClickOutside = (e: MouseEvent) => {
            if (coordinatePopupRef.current && !coordinatePopupRef.current.contains(e.target as Node)) {
                setRightClickPosition(null);
            }
        };

        // Add listener with a small delay to avoid immediate close from the opening right-click
        const timeoutId = setTimeout(() => {
            document.addEventListener("mousedown", handleClickOutside);
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [rightClickPosition]);

    return (
        <>
            <ModalV2 open={showRiskRatingModal} setOpen={setShowRiskRatingModal}>
                <div className="grid gap-y-4 text-white">
                    <div className="text-lg text-dark-dark-grey text-center font-bold">
                       Risk Ratings
                    </div>
                    <div className="grid grid-cols-[10em,1fr] px-4 text-dark-dark-grey">
                        <div className="font-bold">
                            Risk Rating
                        </div>
                        <div className="font-bold">
                            Information
                        </div>
                    </div>

                    {filterParams.feedType === "samdesk" ? (
                        <>
                            {/* <div className="grid grid-cols-[10em,1fr] bg-[#aaaaaa] p-4 rounded-md">*/}
                            {/*    <div className="font-bold">*/}
                            {/*        Unknown*/}
                            {/*    </div>*/}
                            {/*    <div>*/}
                            {/*        Placeholder text.*/}
                            {/*    </div>*/}
                            {/* </div>*/}

                            <div className="grid grid-cols-[10em,1fr] bg-[#1e9b26] p-4 rounded-md">
                                <div className="font-bold">
                                    Low
                                </div>
                                <div>
                                    Minor disruption or limited impact (useful for background awareness)
                                </div>
                            </div>

                            <div className="grid grid-cols-[10em,1fr] bg-[#e6920a] p-4 rounded-md">
                                <div className="font-bold">
                                    Medium
                                </div>
                                <div>
                                    Limited casualties or disruption affecting the public or operations (prioritized monitoring)
                                </div>
                            </div>

                            <div className="grid grid-cols-[10em,1fr] bg-[#a15fb2] p-4 rounded-md">
                                <div className="font-bold">
                                    High
                                </div>
                                <div>
                                    Numerous casualties or major disruption, or smaller incidents in major public locations (immediate escalation)
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="grid grid-cols-[10em,1fr] bg-[#aaaaaa] p-4 rounded-md">
                                <div className="font-bold">
                                    Unknown
                                </div>
                                <div>
                                    The severity has not been determined.
                                </div>
                            </div>
                            <div className="grid grid-cols-[10em,1fr] bg-[#1e9b26] p-4 rounded-md">
                                <div className="font-bold">
                                    Insignificant
                                </div>
                                <div>
                                    No injuries or health effects. Minimal financial loss. Negligible disruption to business
                                    operations. Impacts can be <strong>resolved</strong>.
                                </div>
                            </div>

                            <div className="grid grid-cols-[10em,1fr] bg-[#d8b700] p-4 rounded-md">
                                <div className="font-bold">
                                    Limited
                                </div>
                                <div>
                                    Minimal injuries or health effects. Minor financial loss. Brief interruption in business
                                    operations, quickly <strong>restored</strong>. Impacts are <strong>largely resolved</strong>.
                                </div>
                            </div>
                            <div className="grid grid-cols-[10em,1fr] bg-[#e6920a] p-4 rounded-md">
                                <div className="font-bold">
                                    Moderate
                                </div>
                                <div>
                                    Moderate injuries or health effects. Some financial loss. Business operations face
                                    <strong>short-term disruption</strong>. Impacts are <strong>somewhat resolved</strong>.
                                </div>
                            </div>
                            <div className="grid grid-cols-[10em,1fr] bg-[#9d0404] p-4 rounded-md">
                                <div className="font-bold">
                                    Major
                                </div>
                                <div>
                                    Severe injuries or health effects. Considerable financial loss. Significant business
                                    interruption lasting a couple of <strong>workdays</strong>.
                                    Some impacts may be <strong>permanent</strong>.
                                </div>
                            </div>
                            <div className="grid grid-cols-[10em,1fr] bg-[#a15fb2] p-4 rounded-md">
                                <div className="font-bold">
                                    Extreme
                                </div>
                                <div>
                                    Loss of life or severe injuries. Substantial financial loss. Prolonged business
                                    interruption lasting <strong>more than one business week</strong>. A portion of the impacts
                                    are <strong>lasting</strong>.
                                </div>
                            </div>
                        </>
                    )}

                </div>
            </ModalV2>
            <div className="flex flex-col gap-4 flex-1 h-full">
                <div className="bg-white p-4 rounded shadow">
                    <div className="flex gap-6 items-center flex-wrap">
                        <TimeRangeFilter label="Time Range" columnIndex={0} onChange={handleFilterChange(0)}
                                         filterParams={filterParams}/>
                        {feedCount !== null && feedCount > 1 && (
                            <IncidentFeedFilter label="Feed" columnIndex={1} onChange={handleFilterChange(4)}
                                                filterParams={filterParams}/>
                        )}
                        {filterParams.feedIDs && Array.isArray(filterParams.feedIDs) && filterParams.feedIDs.length > 0 && (
                            <IncidentTypeTagFilter label="Type/Tags" columnIndex={2} onChange={handleFilterChange(1)}
                                                   filterParams={filterParams}/>
                        )}
                        {filterParams.feedType === "intelligence_fusion" && (
                            <SeverityFilter label="Severity" columnIndex={3} onChange={handleFilterChange(2)}
                                            filterParams={filterParams}/>
                        )}
                        {/* <CountryLocationFilter label="Location" columnIndex={4} onChange={handleFilterChange(3)}*/}
                        {/*                       filterParams={filterParams}/>*/}
                        <OptionsFilter label="Options" columnIndex={5} onChange={handleFilterChange(5)}
                                       filterParams={filterParams} forceClustering={incidents.length >= 5000}/>
                        <div className="ml-auto flex items-center gap-3">
                            {filterParams.feedIDs && Array.isArray(filterParams.feedIDs) && filterParams.feedIDs.length > 0 && (
                                <div className="flex items-center gap-2">
                                    <div className={`w-2 h-2 rounded-full ${
                                        storeLoading
                                            ? "bg-yellow-500 animate-pulse"
                                            : wsConnectionState === ConnectionState.CONNECTED
                                                ? "bg-green-500"
                                                : wsConnectionState === ConnectionState.CONNECTING || wsConnectionState === ConnectionState.RECONNECTING
                                                    ? "bg-yellow-500 animate-pulse"
                                                    : "bg-red-500"
                                    }`} />
                                    <span className="text-xs text-gray-500">
                                        {storeLoading
                                            ? "Loading store..."
                                            : wsConnectionState === ConnectionState.CONNECTED
                                                ? "Connected"
                                                : wsConnectionState === ConnectionState.CONNECTING
                                                    ? "Connecting..."
                                                    : wsConnectionState === ConnectionState.RECONNECTING
                                                        ? "Reconnecting..."
                                                        : "Disconnected"
                                        }
                                    </span>
                                </div>
                            )}
                            <div className="text-sm text-gray-600">
                                {!filterParams.feedIDs ||
                                 !Array.isArray(filterParams.feedIDs) ||
                                 filterParams.feedIDs.length === 0
                                    ? "Select a feed to view incidents"
                                    : storeLoading || loading
                                        ? ""
                                        : `${incidents.length} incidents`
                                }
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded shadow flex-1 overflow-hidden relative">
                    {googleLoaded ? (
                        <>
                            <button
                                onClick={() => setShowRiskRatingModal(true)}
                                className="absolute left-4 top-4 rounded-md w-12 h-12 hover:opacity-80 bg-white shadow-md z-50 flex items-center justify-center">
                                <KeyIcon/>
                            </button>

                            {/* Location Search */}
                            <div className="absolute top-4 right-4 z-40 w-80">
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    placeholder="Search for a location..."
                                    className="w-full px-4 py-2 rounded-md shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                />
                            </div>

                            {/* Custom Map Type Selector */}
                            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-50">
                                <div className="bg-white rounded-lg shadow-md flex overflow-hidden">
                                    <button
                                        onClick={() => setMapType("roadmap")}
                                        className={`px-4 py-2 text-sm font-medium transition-colors ${
                                            mapType === "roadmap"
                                                ? "bg-blue text-white"
                                                : "bg-white text-grey hover:bg-light-light-grey"
                                        }`}
                                    >
                                        Map
                                    </button>
                                    <button
                                        onClick={() => setMapType("terrain")}
                                        className={`px-4 py-2 text-sm font-medium transition-colors border-l border-light-light-grey ${
                                            mapType === "terrain"
                                                ? "bg-blue text-white"
                                                : "bg-white text-grey hover:bg-light-light-grey"
                                        }`}
                                    >
                                        Terrain
                                    </button>
                                    <button
                                        onClick={() => setMapType("hybrid")}
                                        className={`px-4 py-2 text-sm font-medium transition-colors border-l border-light-light-grey ${
                                            mapType === "hybrid"
                                                ? "bg-blue text-white"
                                                : "bg-white text-grey hover:bg-light-light-grey"
                                        }`}
                                    >
                                        Hybrid
                                    </button>
                                    <button
                                        onClick={() => setMapType("satellite")}
                                        className={`px-4 py-2 text-sm font-medium transition-colors border-l border-light-light-grey ${
                                            mapType === "satellite"
                                                ? "bg-blue text-white"
                                                : "bg-white text-grey hover:bg-light-light-grey"
                                        }`}
                                    >
                                        Satellite
                                    </button>
                                </div>
                            </div>

                            <div
                                ref={mapDivRef}
                                className="absolute inset-0 w-full h-full"
                                style={{backgroundColor: "#6cd2e7"}}
                            />
                        </>
                    ) : (
                        <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-gray-50">
                            <LoadingSpinner/>
                            <p className="ml-4 text-gray-600">Loading map...</p>
                        </div>
                    )}
                </div>

                {tooltipVisible && !popupVisible && (
                    <div
                        className="fixed bg-dark-dark-grey text-white text-xs px-2 py-1 rounded pointer-events-none z-[160]"
                        style={{
                            left: `${tooltipPosition.x + 10}px`,
                            top: `${tooltipPosition.y - 30}px`,
                        }}
                    >
                        {tooltipCount} incident{tooltipCount !== 1 ? "s" : ""}
                    </div>
                )}

                {popupVisible && (
                    <div
                        ref={popupRef}
                        className="fixed bg-white rounded shadow-lg z-[150] w-80 max-w-md"
                        style={{
                            left: `${popupPosition.x}px`,
                            top: `${popupPosition.y}px`,
                        }}
                    >
                        <div className="p-2 bg-light-light-grey border-b font-semibold text-sm">
                            Select Incident ({popupTotalCount})
                        </div>
                        <div>
                            {popupIncidents.map((inc) => {
                                const formatTime = (dateString: string) => {
                                    if (inc.timezone) {
                                        const formatter = new Intl.DateTimeFormat("en-GB", {
                                            timeZone: inc.timezone,
                                            year: "numeric",
                                            month: "2-digit",
                                            day: "2-digit",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                            timeZoneName: "short"
                                        });
                                        return formatter.format(new Date(dateString));
                                    }
                                    return new Date(dateString).toLocaleString();
                                };

                                return (
                                    <button
                                        key={inc.id}
                                        onClick={() => {
                                            modalFromPopupRef.current = true;
                                            setPopupVisible(false);
                                            setTooltipVisible(false);

                                            if (wsManagerRef.current && wsManagerRef.current.isConnected()) {
                                                wsManagerRef.current.send("getIncident", {
                                                    incidentID: inc.id
                                                });
                                            }
                                        }}
                                        className="w-full text-left p-3 hover:bg-light-blue border-b border-light-light-grey last:border-b-0 transition-colors"
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className="flex-shrink-0 mt-0.5">
                                                <IncidentSeverityDisplay severity={inc.severity} tags={inc.tags}/>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-medium text-sm mb-1 line-clamp-2">
                                                    {inc.summary}
                                                </div>
                                                <div className="text-xs text-gray-600 mb-1">
                                                    {inc.categoryName}{inc.typeName ? ` - ${inc.typeName}` : ""}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {formatTime(inc.startedAt)}
                                                </div>
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                        {popupTotalCount > POPUP_PAGE_SIZE && (
                            <div className="flex items-center justify-between px-3 py-2 border-t bg-light-light-grey text-sm">
                                <button
                                    onClick={() => {
                                        const newPage = popupPage - 1;
                                        const pageIDs = popupAllIDsRef.current.slice((newPage - 1) * POPUP_PAGE_SIZE, newPage * POPUP_PAGE_SIZE);
                                        if (wsManagerRef.current && wsManagerRef.current.isConnected()) {
                                            pendingIncidentDetailsRef.current = {
                                                incidentIDs: pageIDs,
                                                callback: (previewIncidents: any[]) => {
                                                    setPopupIncidents(previewIncidents);
                                                    setPopupPage(newPage);
                                                }
                                            };
                                            wsManagerRef.current.send("getIncidentsPreview", { incidentIDs: pageIDs });
                                        }
                                    }}
                                    disabled={popupPage === 1}
                                    className="px-2 py-1 rounded disabled:opacity-30 hover:bg-light-blue transition-colors"
                                >
                                    ‹ Prev
                                </button>
                                <span className="text-xs text-gray-600">
                                    {popupPage} / {Math.ceil(popupTotalCount / POPUP_PAGE_SIZE)}
                                </span>
                                <button
                                    onClick={() => {
                                        const newPage = popupPage + 1;
                                        const pageIDs = popupAllIDsRef.current.slice((newPage - 1) * POPUP_PAGE_SIZE, newPage * POPUP_PAGE_SIZE);
                                        if (wsManagerRef.current && wsManagerRef.current.isConnected()) {
                                            pendingIncidentDetailsRef.current = {
                                                incidentIDs: pageIDs,
                                                callback: (previewIncidents: any[]) => {
                                                    setPopupIncidents(previewIncidents);
                                                    setPopupPage(newPage);
                                                }
                                            };
                                            wsManagerRef.current.send("getIncidentsPreview", { incidentIDs: pageIDs });
                                        }
                                    }}
                                    disabled={popupPage === Math.ceil(popupTotalCount / POPUP_PAGE_SIZE)}
                                    className="px-2 py-1 rounded disabled:opacity-30 hover:bg-light-blue transition-colors"
                                >
                                    Next ›
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {rightClickPosition && (
                    <div
                        ref={coordinatePopupRef}
                        className="fixed bg-white rounded shadow-lg z-[150] p-4 min-w-[200px]"
                        style={{
                            left: `${rightClickPosition.x}px`,
                            top: `${rightClickPosition.y}px`,
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="font-semibold text-sm mb-2">Coordinates</div>
                        <div className="text-xs text-gray-600 mb-1">
                            <span className="font-medium">Latitude:</span> {rightClickPosition.lat.toFixed(6)}
                        </div>
                        <div className="text-xs text-gray-600 mb-3">
                            <span className="font-medium">Longitude:</span> {rightClickPosition.lng.toFixed(6)}
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(`${rightClickPosition.lat},${rightClickPosition.lng}`);
                                }}
                                className="px-3 py-1 bg-blue text-white rounded text-xs hover:bg-blue-600 transition-colors"
                            >
                                Copy
                            </button>
                            <button
                                onClick={() => setRightClickPosition(null)}
                                className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )}

                <IncidentDetailsModal
                    incident={selectedIncident}
                    open={modalOpen}
                    setOpen={(open) => {
                        setModalOpen(open);
                        if (!open && modalFromPopupRef.current) {
                            modalFromPopupRef.current = false;
                            setPopupVisible(true);
                        }
                    }}
                />
            </div>
        </>
    );
}
